import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Block as SuspendIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  PlayArrow as UnsuspendIcon,
  Payment as PaidIcon,
  MoneyOff as RefundIcon,
  RateReview as ChangesIcon,
  Cancel as RejectIcon,
  RocketLaunch as GoLiveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const statusColor = (s) => {
  const m = {
    active: 'success',
    scheduled: 'info',
    pending_payment: 'warning',
    pending_approval: 'warning',
    suspended: 'error',
    rejected: 'error',
    expired: 'default',
    draft: 'default',
  };
  return m[s] || 'default';
};

const BusinessPostsAdmin = () => {
  const [tab, setTab] = useState('live');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [dialogMode, setDialogMode] = useState(null); // suspend | refund | reject | request_changes | analytics

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { tab, limit: 100 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${API_BASE_URL}/business-posts`, {
        params,
        headers: authHeaders(),
      });
      setRows(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter, search]);

  useEffect(() => {
    document.title = 'Posts By Businesses - LOCSY';
    loadRows();
  }, [loadRows]);

  const moderate = async (id, action, extra = {}) => {
    try {
      await axios.post(
        `${API_BASE_URL}/business-posts/${id}/moderate`,
        { action, notes: actionNotes, ...extra },
        { headers: authHeaders() },
      );
      toast.success(`Action: ${action}`);
      setSelected(null);
      setDialogMode(null);
      setActionNotes('');
      setAnalytics(null);
      loadRows();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    }
  };

  const openAction = (post, mode) => {
    setSelected(post);
    setDialogMode(mode);
    setAnalytics(null);
    setActionNotes(post.adminRemarks || '');
  };

  const loadAnalytics = async (post) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/business-posts/analytics/summary`, {
        params: { postId: post.id, businessId: post.businessId },
        headers: authHeaders(),
      });
      setAnalytics(res.data?.data || null);
      setSelected(post);
      setDialogMode('analytics');
    } catch (err) {
      toast.error('Analytics failed');
    }
  };

  const dialogTitle = {
    suspend: 'Pause ad (admin override)',
    delete: 'Delete ad permanently',
    refund: 'Refund & reject ad',
    reject: 'Reject ad',
    request_changes: 'Request changes from shop',
    analytics: 'Post analytics',
  }[dialogMode] || 'Post';

  return (
    <DashboardLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Typography variant="h5" fontWeight={800} gutterBottom>
          Posts By Businesses
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Manage every Mitra ad — approve, pause, delete, refund. Shops see remarks in Mitra Manage My Ads.
        </Typography>
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <strong>App admin decision is final.</strong> You can <strong>Pause</strong> or{' '}
          <strong>Delete</strong> any ad — including live — without shop consent when needed for
          policy / legal / quality. Shop cannot resume an admin pause.
        </Alert>
        <Alert severity="info" sx={{ mb: 2 }}>
          After you <strong>Approve</strong> a pending ad it becomes <strong>active</strong> and
          appears in the citizen app (Live chip). Dummy/sample cards keep a <strong>Demo ads</strong> chip.
          Category pages need the <strong>category_featured</strong> placement.
          Use rocket <strong>Go live now</strong> only to force a still-scheduled ad live.
        </Alert>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab value="live" label="Live posts" />
          <Tab value="demo" label="Demo posts" />
        </Tabs>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
          <TextField
            size="small"
            placeholder="Search title, shop, or contact…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadRows()}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="pending_approval">Pending approval</MenuItem>
              <MenuItem value="pending_payment">Pending payment</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="draft">Draft / changes</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
              <MenuItem value="rejected">Rejected / refunded</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<RefreshIcon />} onClick={loadRows}>
            Refresh
          </Button>
        </Stack>

        {loading ? (
          <Typography>Loading…</Typography>
        ) : (
          <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small" sx={{ minWidth: { xs: 920, md: 1120 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Vyapar / Contact</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Placements</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Pay</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Alert severity="info">No posts in this tab.</Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ minWidth: { xs: 205, sm: 245 } }}>
  <Typography variant="body2" fontWeight={700}>
    {r.shopName || r.businessName || 'Unknown shop'}
  </Typography>
  <Typography variant="caption" display="block">Proprietor: {r.proprietorName || '—'}</Typography>
  <Typography variant="caption" display="block">Mobile: {r.mobile || '—'}</Typography>
  <Typography variant="caption" display="block">City: {r.city || '—'}</Typography>
  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
    {r.mobile && <Button size="small" component="a" href={`tel:${r.mobile}`} sx={{ minWidth: 0, px: 0.75 }}>Call</Button>}
    {r.whatsapp && <Button size="small" component="a" target="_blank" rel="noreferrer" href={`https://wa.me/${String(r.whatsapp).replace(/\D/g, '')}`} sx={{ minWidth: 0, px: 0.75 }}>WhatsApp</Button>}
  </Stack>
</TableCell>
                      <TableCell>{r.title}</TableCell>
                      <TableCell>{r.postType}</TableCell>
                      <TableCell>
                        {(r.placements || []).map((p) => (
                          <Chip key={p} label={p} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.status} color={statusColor(r.status)} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.paymentStatus || '—'} variant="outlined" />
                      </TableCell>
                      <TableCell>₹{r.amountInr}</TableCell>
                      <TableCell sx={{ maxWidth: 160 }}>
                        <Typography variant="caption" noWrap title={r.adminRemarks || ''}>
                          {r.adminRemarks || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => loadAnalytics(r)} title="Analytics">
                          <ViewIcon />
                        </IconButton>
                        {(r.status === 'pending_approval' ||
                          r.status === 'draft' ||
                          r.status === 'scheduled' ||
                          (r.status !== 'active' && r.status !== 'suspended')) && (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => moderate(r.id, 'approve')}
                            title="Approve / publish"
                          >
                            <ApproveIcon />
                          </IconButton>
                        )}
                        {(r.status === 'scheduled' || r.status === 'pending_approval') &&
                          (r.paymentStatus === 'paid' || r.paymentStatus === 'not_required') && (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => moderate(r.id, 'go_live')}
                            title="Go live now (ignore schedule)"
                          >
                            <GoLiveIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => openAction(r, 'request_changes')}
                          title="Request changes"
                        >
                          <ChangesIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openAction(r, 'reject')}
                          title="Reject"
                        >
                          <RejectIcon />
                        </IconButton>
                        {(r.paymentStatus === 'paid' || r.paymentStatus === 'pending') && (
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => openAction(r, 'refund')}
                            title="Refund"
                          >
                            <RefundIcon />
                          </IconButton>
                        )}
                        {r.status !== 'suspended' ? (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openAction(r, 'suspend')}
                            title="Pause now (live ads included)"
                          >
                            <SuspendIcon />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => moderate(r.id, 'unsuspend')}
                            title="Resume (unsuspend)"
                          >
                            <UnsuspendIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openAction(r, 'delete')}
                          title="Delete permanently"
                        >
                          <DeleteIcon />
                        </IconButton>
                        {r.paymentStatus === 'pending' && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={async () => {
                              try {
                                await axios.post(
                                  `${API_BASE_URL}/business-posts/${r.id}/payment/mark-paid`,
                                  {},
                                  { headers: authHeaders() },
                                );
                                toast.success('Marked as paid');
                                loadRows();
                              } catch (err) {
                                toast.error(err.response?.data?.message || 'Failed');
                              }
                            }}
                            title="Mark paid (offline)"
                          >
                            <PaidIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={Boolean(selected) && Boolean(dialogMode)}
          onClose={() => {
            setSelected(null);
            setDialogMode(null);
            setAnalytics(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogContent>
            {dialogMode === 'analytics' && analytics ? (
              <Stack spacing={1}>
                <Typography variant="body2">Views: {analytics.totalViews}</Typography>
                <Typography variant="body2">Unique: {analytics.uniqueViews}</Typography>
                <Typography variant="body2">CTR: {analytics.ctr}%</Typography>
                <Typography variant="body2">
                  Clicks: {JSON.stringify(analytics.clicksByType || {})}
                </Typography>
              </Stack>
            ) : (
              <Box>
                {selected && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {selected.businessName || selected.businessId} — {selected.title}
                    <br />
                    Status: {selected.status} · Pay: {selected.paymentStatus} · ₹{selected.amountInr}
                  </Alert>
                )}
                {dialogMode === 'suspend' && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Pausing removes the ad from citizen apps immediately. Shop cannot resume an admin pause.
                    Remarks are optional.
                  </Alert>
                )}
                {dialogMode === 'delete' && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Permanent delete — ad is removed from citizen apps and Manage My Ads. This cannot be undone.
                  </Alert>
                )}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={
                    dialogMode === 'suspend' || dialogMode === 'delete'
                      ? 'Internal / shop note (optional)'
                      : 'Remarks to shop (required for changes / refund)'
                  }
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  sx={{ mt: 1 }}
                  placeholder={
                    dialogMode === 'suspend'
                      ? 'Optional reason shown to shop…'
                      : dialogMode === 'delete'
                        ? 'Optional delete note…'
                        : 'Explain what to change, or refund reason…'
                  }
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setSelected(null);
                setDialogMode(null);
                setAnalytics(null);
              }}
            >
              Close
            </Button>
            {dialogMode === 'suspend' && selected && (
              <Button
                color="error"
                variant="contained"
                onClick={() => moderate(selected.id, 'suspend')}
              >
                Pause now
              </Button>
            )}
            {dialogMode === 'delete' && selected && (
              <Button
                color="error"
                variant="contained"
                onClick={() => moderate(selected.id, 'delete')}
              >
                Delete permanently
              </Button>
            )}
            {dialogMode === 'reject' && selected && (
              <Button color="error" variant="contained" onClick={() => moderate(selected.id, 'reject')}>
                Reject
              </Button>
            )}
            {dialogMode === 'refund' && selected && (
              <Button color="secondary" variant="contained" onClick={() => moderate(selected.id, 'refund')}>
                Mark refunded
              </Button>
            )}
            {dialogMode === 'request_changes' && selected && (
              <Button
                color="warning"
                variant="contained"
                onClick={() => {
                  if (!actionNotes.trim()) {
                    toast.error('Add remarks for the shop');
                    return;
                  }
                  moderate(selected.id, 'request_changes');
                }}
              >
                Send remarks
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default BusinessPostsAdmin;
